import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * A normalized party (seller/buyer) for the shared preview list. Callers map their own shape —
 * a KSeF `KsefParty` on the read-only detail page, a saved `Customer` in the composer — onto this.
 */
export interface PartyView {
  name?: string | null;
  taxId?: string | null;
  /** Already-formatted address lines; blanks are dropped and the rest joined with newlines. */
  addressLines?: Array<string | null | undefined>;
  countryCode?: string | null;
  email?: string | null;
  phone?: string | null;
}

/**
 * One party's identity as a label/value list — name, tax id, address, country, e-mail, phone. Shared
 * so the invoice-detail preview and the composer's buyer card render byte-for-byte the same way; only
 * fields we hold are shown.
 */
export function PartyDetails({ party }: { party: PartyView }) {
  const { t } = useTranslation();
  const address = (party.addressLines ?? []).filter(
    (line): line is string => line != null && line.trim() !== '',
  );

  return (
    <dl className="divide-y">
      {party.name ? (
        <PartyField label={t('invoices.details.party.name')}>{party.name}</PartyField>
      ) : null}
      {party.taxId ? (
        <PartyField label={t('invoices.details.party.tax_id')}>{party.taxId}</PartyField>
      ) : null}
      {address.length ? (
        <PartyField label={t('invoices.details.party.address')}>
          <span className="whitespace-pre-line">{address.join('\n')}</span>
        </PartyField>
      ) : null}
      {party.countryCode ? (
        <PartyField label={t('invoices.details.party.country')}>{party.countryCode}</PartyField>
      ) : null}
      {party.email ? (
        <PartyField label={t('invoices.details.party.email')}>
          <a className="underline underline-offset-2" href={`mailto:${party.email}`}>
            {party.email}
          </a>
        </PartyField>
      ) : null}
      {party.phone ? (
        <PartyField label={t('invoices.details.party.phone')}>{party.phone}</PartyField>
      ) : null}
    </dl>
  );
}

function PartyField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 px-3 py-2 text-sm">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-right font-medium">{children}</dd>
    </div>
  );
}
